/**
 * Estado de un sensor tal como se muestra en pantalla.
 *
 * Cuatro estados y cuatro colores, en este orden de prioridad:
 *
 *   gris     `sin_datos`   nunca ha llegado una sola lectura de este sensor
 *   amarillo `sin_latido`  el nodo que lo lleva no está dando señal de vida
 *   rojo     `alarma`      hay lectura, hay umbral y el umbral está roto
 *   verde    `normal`      todo lo demás
 *
 * El orden importa: un sensor sin lecturas es gris aunque su nodo esté caído
 * —nunca ha dicho nada, no hay nada que juzgar—, y un sensor cuyo nodo no
 * responde es amarillo aunque su última lectura estuviera en alarma, porque
 * esa lectura es de antes de perderse el latido y darla por buena sería
 * afirmar algo que ya no se sabe.
 *
 * Antes había un quinto estado, «sin umbral configurado», que pintaba de gris
 * a sensores que medían perfectamente. Un sensor sin umbral ya no es un caso
 * aparte: mide y está normal; lo que no tiene es contra qué compararse, y eso
 * se ve en su ficha, no en el color.
 */

export const ESTADOS_SENSOR = ['normal', 'alarma', 'sin_latido', 'sin_datos'] as const;

export type EstadoSensorValor = (typeof ESTADOS_SENSOR)[number];

const ETIQUETA: Readonly<Record<EstadoSensorValor, string>> = {
  normal: 'Normal',
  alarma: 'En alarma',
  sin_latido: 'Sin latido',
  sin_datos: 'Nunca ha reportado',
};

const TOKEN: Readonly<Record<EstadoSensorValor, string>> = {
  normal: '--status-success',
  alarma: '--status-danger',
  sin_latido: '--status-warning',
  sin_datos: '--text-3',
};

/** Mayor número = más urgente. Sirve para ordenar listas por gravedad. */
const URGENCIA: Readonly<Record<EstadoSensorValor, number>> = {
  alarma: 3,
  sin_latido: 2,
  sin_datos: 1,
  normal: 0,
};

export class EstadoSensor {
  private constructor(readonly valor: EstadoSensorValor) {}

  static normal(): EstadoSensor {
    return new EstadoSensor('normal');
  }
  static alarma(): EstadoSensor {
    return new EstadoSensor('alarma');
  }
  static sinLatido(): EstadoSensor {
    return new EstadoSensor('sin_latido');
  }
  static sinDatos(): EstadoSensor {
    return new EstadoSensor('sin_datos');
  }

  static from(raw: string | null | undefined): EstadoSensor {
    const limpio = (raw ?? '').trim().toLowerCase().replace(/-/g, '_');
    const valor = (ESTADOS_SENSOR as readonly string[]).includes(limpio)
      ? (limpio as EstadoSensorValor)
      : 'sin_datos';
    return new EstadoSensor(valor);
  }

  get etiqueta(): string {
    return ETIQUETA[this.valor];
  }

  get color(): string {
    return `var(${TOKEN[this.valor]})`;
  }

  get urgencia(): number {
    return URGENCIA[this.valor];
  }

  /** El sensor está midiendo y dentro de lo esperado. */
  get esNormal(): boolean {
    return this.valor === 'normal';
  }

  /** Requiere que alguien mire: o saltó, o dejó de hablar. */
  get requiereAtencion(): boolean {
    return this.valor === 'alarma' || this.valor === 'sin_latido';
  }

  /** No hay información fiable con la que juzgar el valor que se muestra. */
  get esIndeterminado(): boolean {
    return this.valor === 'sin_datos' || this.valor === 'sin_latido';
  }

  equals(otro: EstadoSensor): boolean {
    return this.valor === otro.valor;
  }

  static comparar(a: EstadoSensor, b: EstadoSensor): number {
    return b.urgencia - a.urgencia;
  }

  toString(): string {
    return this.valor;
  }
}
